//create a generic global handler for communication errors that go unresolved.
import { ApiError } from '../../webmodule-common/interop/problem-details';
import { GlobalSupplierNotification } from '../v6config/supplier-services';
import { handleOutOfDateIntercept } from '../../webmodule-common/other/common/web-cache';
import { information } from '../../webmodule-common/other/ui/modal-option';
import { LangApiResponseHandler } from '../../localization/scripts/api/api-response-handler-lang';
import { LangUserSessionVerifier } from '../../localization/webmodule-common/other/user-session-verifier-lang';
import { pageReload } from '../../webmodule-common/other/ui/resource-resolver';
import { setCurrentUser } from '../../webmodule-common/other/api/current-user';
import { showError } from '../../webmodule-common/other/ui/show-error';
import { showValidations } from '../../webmodule-common/other/ui/modal-validationhandler';
import {
  strDuplicateLogin,
  strNetworkUnavailable,
  strRequestTokenMismatch,
  strServiceRestarting,
  strUnableToConnectToV6
} from '../../webmodule-common/other/api/network-consts';
import { V6OfflineCause } from '../quotes/quote-service';
import { ValidationError } from '../../webmodule-common/other/api/validation-error';

let handlingDuplicateLogin = false;

// ProblemDetails type patterns
const ProblemTypes = {
  VersionMismatch: 'version-mismatch',
  AlreadyConnected: 'already-connected',
  UnAuthenticated: 'unauthenticated',
  DatabaseError: 'database-error'
};

//this will typically only be things we dont want to happen not considered normal workflow
export const responseHandler = async (response: ApiError | ValidationError[]) => {
  // Handle validation errors array
  if (Array.isArray(response)) {
    console.log('Service Response Validation Errors Returned');
    const msg: string[] = [];
    response.forEach(err => {
      const errMsg = `${err.property} -> ${err.message}`;
      msg.push(errMsg);
      console.log(errMsg);
    });
    await showValidations(msg, () => LangApiResponseHandler.validationIssuesFromServer());
    return;
  }

  // Handle ApiError with ProblemDetails
  if (response instanceof ApiError) {
    const pd = response.problemDetails;
    const detail = pd.detail ?? '';

    // Version mismatch - reload required
    if (pd.type.includes(ProblemTypes.VersionMismatch)) {
      try {
        await handleOutOfDateIntercept(true);
      } finally {
        pageReload();
      }
      return;
    }

    // Already connected / duplicate login (HTTP 412)
    if (pd.type.includes(ProblemTypes.AlreadyConnected) || pd.status === 412 || detail === strDuplicateLogin) {
      if (handlingDuplicateLogin) return;
      handlingDuplicateLogin = true;
      try {
        await information(LangApiResponseHandler.duplicateLogin());
        await setCurrentUser(null);
      } finally {
        handlingDuplicateLogin = false;
      }
      return;
    } // Request token mismatch - user switched accounts in another tab

    if (detail.includes(strRequestTokenMismatch)) {
      if (handlingDuplicateLogin) return;
      handlingDuplicateLogin = true;
      try {
        await information(LangUserSessionVerifier.userSwitched());
        window.location.reload();
      } finally {
        handlingDuplicateLogin = false;
      }
      return;
    }
    // V6 connection issues
    if (detail.startsWith(strUnableToConnectToV6)) {
      await GlobalSupplierNotification.getInstance().makeOffline(V6OfflineCause.Connection, detail);
      return;
    }

    if (detail === 'V6 Service upgrade in progress') {
      await GlobalSupplierNotification.getInstance().makeOffline(V6OfflineCause.Upgrading, detail);
      return;
    }

    // Network unavailable or service restarting
    if (detail.includes(strNetworkUnavailable) || detail.includes(strServiceRestarting)) {
      console.log('API Response: Network unavailable or service restarting - handled by event source connection state');
      return;
    }

    // Log and display the error
    console.log(`ProblemDetails Error [${pd.status}] "${pd.title}" - ${pd.detail}`);
    if (pd.stackTrace) {
      console.log(`StackTrace: ${pd.stackTrace}`);
    }

    await showError(response, () => LangApiResponseHandler.serverErrorOccurred());
    return;
  }

  // Fallback: unknown error type
  console.error('Unknown error type passed to responseHandler:', response);
};
